import { createAsyncThunk } from "@reduxjs/toolkit";
import { EmployeeProfile, PasswordSetupStatus } from "../../../types/model/profile";
import { phpClient } from "../../../network";
import { PhpResponse, IRejectWithValue } from "../../../types";
import { GetEmployeeProfileRequest, UpdateEmployeeProfileRequest } from "../../../types/request";

/**
 * Get employee profile by employee ID
 */
export const getEmployeeProfile = createAsyncThunk<EmployeeProfile, GetEmployeeProfileRequest, IRejectWithValue>(
    "profile/getEmployeeProfile",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<EmployeeProfile[]>>({
                url: `/employees/${request.employee_id}`,
            });
            
            if (!response.data || response.data.length === 0) {
                return rejectWithValue(response.message || "No employee profile found");
            }
            
            // API returns an array but we expect a single profile
            return response.data[0];
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || "An error occurred while fetching employee profile");
        }
    }
);

/**
 * Get safe password setup decision details for the logged-in employee
 */
export const fetchPasswordSetupStatus = createAsyncThunk<
    PasswordSetupStatus,
    GetEmployeeProfileRequest,
    IRejectWithValue
>(
    "profile/fetchPasswordSetupStatus",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<PasswordSetupStatus>>({
                url: `/employees/password_setup_status/${request.employee_id}`,
            });

            if (!response.data) {
                return rejectWithValue(response.message || "No password setup status received");
            }

            return response.data;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "An error occurred while fetching password setup status"
            );
        }
    }
);

/**
 * Update employee profile by employee ID
 */
export const updateEmployeeProfile = createAsyncThunk<
    { message?: string },
    UpdateEmployeeProfileRequest,
    IRejectWithValue
>(
    "profile/updateEmployeeProfile",
    async (request, { rejectWithValue }) => {
        try {
            const { employee_id, ...updateData } = request;
            
            const response = await phpClient.post<PhpResponse<EmployeeProfile>>({
                url: `/employees/update/${employee_id}`,
                data: updateData,
            });

            if (response.status === false) {
                return rejectWithValue(response.message || "Unable to update employee profile");
            }
            
            return {
                message: response.message,
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || "An error occurred while updating employee profile");
        }
    }
);
